'use client'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitButton } from '@/components/Buttons/Buttons'
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/lib/validations/auth'
import Link from 'next/link'
import { ReactElement, useState } from 'react'

export default function ForgotPassword(): ReactElement {
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError(null)
    setMessage(null)

    const res = await fetch('/api/members/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: data.email }),
    })
    const result = await res.json()
    setIsLoading(false)

    if (result.error) {
      setError(result.error)
      return
    }
    if (res.ok) {
      setMessage('Please check your email for a password reset link.')
    }
  }

  return (
    <div className="flex min-h-full flex-col justify-center items-center">
      <header className="text-xl md:text-2xl font-bold mb-5">Forgot Password</header>
      <div className="w-full mx-auto md:max-w-sm">
        {message ? (
          <div className="text-center mb-5">{message}</div>
        ) : (
          <>
            <form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col">
                <label htmlFor="email">Email</label>
                <input
                  className={`textInput placeholder-white/50 ${
                    errors.email ? 'border-red-500' : ''
                  }`}
                  type="email"
                  id="email"
                  placeholder="Enter your email"
                  {...register('email')}
                />
                {errors.email && (
                  <span className="text-red-500 text-sm mt-1">{errors.email.message}</span>
                )}
              </div>
              {error && <div className="text-red-600">{error}</div>}

              <SubmitButton
                className="mb-5"
                loading={isLoading}
                text="Reset Password"
                color="primary"
              />
            </form>
            <footer>
              <div className="flex justify-center gap-2">
                <Link href="/login">Login</Link>
                {/* <span>|</span>
                <Link href="/signup">Sign up</Link> */}
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  )
}
